import type { AppSettings } from '../types';

export type GraphiteAccount = {
  id: number; identifier: string; name: string; created_at: string; totp_enabled: boolean;
  subscription: { tier: 'Free' | 'Basic' | 'Pro'; starts_at: string | null; ends_at: string | null };
};

const CACHE_KEY = 'graphite.account.v1';
const accountUrl = (settings: AppSettings, path: string) => `${settings.cloudEndpoint.replace(/\/$/, '')}/v1${path}`;

export function loadCachedAccount(): GraphiteAccount | null {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null') as GraphiteAccount | null; }
  catch { return null; }
}

export function cacheAccount(account: GraphiteAccount | null) {
  if (account) localStorage.setItem(CACHE_KEY, JSON.stringify(account));
  else localStorage.removeItem(CACHE_KEY);
}

export async function loadAccount(settings: AppSettings): Promise<GraphiteAccount | null> {
  const response = await fetch(accountUrl(settings, '/auth/me'), { credentials: 'include' });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`Авторизация недоступна (${response.status})`);
  return response.json() as Promise<GraphiteAccount>;
}

export async function loginAccount(settings: AppSettings, identifier: string, code?: string) {
  const response = await fetch(accountUrl(settings, '/auth/nickname'), { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ account_id: identifier, ...(code ? { totp_code: code } : {}) }) });
  if (!response.ok) throw new Error(response.status === 401 ? 'Неверный код 2FA' : `Не удалось войти (${response.status})`);
  return response.json() as Promise<{ account: GraphiteAccount | null; created: boolean; requires_totp: boolean }>;
}

export async function logoutAccount(settings: AppSettings) {
  await fetch(accountUrl(settings, '/auth/logout'), { method: 'POST', credentials: 'include' });
}

export async function deleteCloudAccount(settings: AppSettings) {
  const response = await fetch(accountUrl(settings, '/account'), { method: 'DELETE', credentials: 'include' });
  if (!response.ok) throw new Error(response.status === 401 ? 'Сессия аккаунта истекла' : `Не удалось удалить облачные данные (${response.status})`);
}

export async function totpRequest(settings: AppSettings, action: 'setup' | 'confirm' | 'disable', code?: string) {
  const response = await fetch(accountUrl(settings, `/auth/totp/${action}`), { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...(code ? { body: JSON.stringify({ code }) } : {}) });
  if (!response.ok) throw new Error(response.status === 401 ? 'Неверный код' : `2FA недоступна (${response.status})`);
  return response.json() as Promise<{ ok?: boolean; secret?: string; provisioning_uri?: string }>;
}

export async function loadBilling(settings: AppSettings) {
  const response = await fetch(accountUrl(settings, '/billing'), { credentials: 'include' });
  if (!response.ok) throw new Error(response.status === 401 ? 'Войдите, чтобы увидеть биллинг' : `Биллинг недоступен (${response.status})`);
  return response.json() as Promise<{ all_time: BillingTotal; month: BillingTotal; services: BillingService[]; events: BillingEvent[] }>;
}

export type BillingTotal = { requests: number; input_tokens: number; output_tokens: number; cost_usd: string | number };
export type BillingService = BillingTotal & { service: string; model: string };
export type BillingEvent = { id: number; trace_id: string | null; request_id: string | null; service: string; endpoint: string; model: string; status: string; input_tokens: number; output_tokens: number; cost_usd: string | number; created_at: string };
